<template>
  <div class="mb-3">
    <h3 class="mb-1 text-lg font-bold">
      {{ $t("settings.tickets-emails.title") }}
    </h3>

    <div class="flex h-[50px] items-center">
      <p class="w-1/3 text-base">{{ $t("printersettings.title") }}</p>
      <div class="flex w-1/3 items-center gap-2">
        <SButton
          type="button"
          class="flex-1"
          :variant="printerOption.auto ? 'default' : 'outline'"
          @click="toggleStatus(true)"
        >
          {{ $t("button.auto") }}
        </SButton>
        <SButton
          type="button"
          class="flex-1"
          :variant="printerOption.auto ? 'outline' : 'default'"
          @click="toggleStatus(false)"
        >
          {{ $t("button.manual") }}
        </SButton>
      </div>
    </div>

    <div class="flex h-[50px] items-center">
      <p class="w-1/3 text-base">{{ $t("printersettings.ticket-type") }}</p>
      <div class="w-1/3">
        <SSelect v-model="selectedTicketType">
          <SSelectTrigger class="w-full">
            <SSelectValue :placeholder="$t('select.ticket-type')" />
          </SSelectTrigger>
          <SSelectContent>
            <SSelectItem
              v-for="ticketType in ticketTypesArray"
              :key="ticketType.value"
              :value="ticketType.value"
            >
              {{ ticketType.label }}
            </SSelectItem>
          </SSelectContent>
        </SSelect>
      </div>
    </div>

    <div class="flex h-[50px] items-center">
      <p class="w-1/3 text-base">{{ $t("printersettings.ticket-printer") }}</p>
      <div class="flex w-1/3 items-center gap-2">
        <SButton
          type="button"
          class="flex-1"
          :variant="ticketPrintingStatus ? 'default' : 'outline'"
          @click="setTicketPrintingStatus(true)"
        >
          {{ $t("button.enabled") }}
        </SButton>
        <SButton
          type="button"
          class="flex-1"
          :variant="!ticketPrintingStatus ? 'default' : 'outline'"
          @click="setTicketPrintingStatus(false)"
        >
          {{ $t("button.disabled") }}
        </SButton>
      </div>
      <div
        v-if="selectedTicketType === TicketTypes.Ticket && !showLocalPrinters"
        class="w-1/3 pl-2"
      >
        <SSelect v-model="printerOption.printer">
          <SSelectTrigger class="w-full">
            <SSelectValue :placeholder="$t('select.printer')" />
          </SSelectTrigger>
          <SSelectContent>
            <SSelectItem
              v-for="item in printers"
              :key="item.printer_id"
              :value="item.printer_id"
            >
              {{ item.printer_name }}
            </SSelectItem>
          </SSelectContent>
        </SSelect>
      </div>
      <div
        v-if="
          showLocalPrinters &&
          ticketPrintingStatus &&
          selectedTicketType !== TicketTypes.MiniTicket
        "
        class="w-1/3 pl-2"
      >
        <SSelect v-model="ticketPrinter">
          <SSelectTrigger class="w-full">
            <SSelectValue :placeholder="$t('select.printer')" />
          </SSelectTrigger>
          <SSelectContent>
            <SSelectItem
              v-for="item in availablePrinters"
              :key="item.name"
              :value="item.name"
            >
              {{ item.displayName }}
            </SSelectItem>
          </SSelectContent>
        </SSelect>
      </div>
    </div>

    <div class="flex h-[50px] items-center">
      <p class="w-1/3 text-base">{{ $t("printersettings.label-printer") }}</p>
      <div class="flex w-1/3 items-center gap-2">
        <SButton
          type="button"
          class="flex-1"
          :variant="labelPrintingStatus ? 'default' : 'outline'"
          @click="setLabelPrintingStatus(true)"
        >
          {{ $t("button.enabled") }}
        </SButton>
        <SButton
          type="button"
          class="flex-1"
          :variant="!labelPrintingStatus ? 'default' : 'outline'"
          @click="setLabelPrintingStatus(false)"
        >
          {{ $t("button.disabled") }}
        </SButton>
      </div>
      <div v-if="labelPrintingStatus" class="w-1/3 pl-2">
        <SSelect v-model="labelPrinter">
          <SSelectTrigger class="w-full">
            <SSelectValue :placeholder="$t('select.printer')" />
          </SSelectTrigger>
          <SSelectContent>
            <SSelectItem
              v-for="item in availablePrinters"
              :key="item.name"
              :value="item.name"
            >
              {{ item.displayName }}
            </SSelectItem>
          </SSelectContent>
        </SSelect>
      </div>
    </div>

    <div class="flex h-[50px] items-center">
      <p class="w-1/3 text-base">{{ $t("settings.order-summary-receipt") }}</p>
      <div class="flex w-1/3 items-center gap-2">
        <SButton
          type="button"
          class="flex-1"
          :variant="orderSummaryReceiptPrintingStatus ? 'default' : 'outline'"
          @click="setOrderSummaryReceiptPrintingStatus(true)"
        >
          {{ $t("button.enabled") }}
        </SButton>
        <SButton
          type="button"
          class="flex-1"
          :variant="!orderSummaryReceiptPrintingStatus ? 'default' : 'outline'"
          @click="setOrderSummaryReceiptPrintingStatus(false)"
        >
          {{ $t("button.disabled") }}
        </SButton>
      </div>
      <div v-if="orderSummaryReceiptPrintingStatus" class="w-1/3 pl-2">
        <SSelect v-model="orderSummaryReceiptPrinter">
          <SSelectTrigger class="w-full">
            <SSelectValue :placeholder="$t('select.printer')" />
          </SSelectTrigger>
          <SSelectContent>
            <SSelectItem
              v-for="item in availablePrinters"
              :key="item.name"
              :value="item.name"
            >
              {{ item.displayName }}
            </SSelectItem>
          </SSelectContent>
        </SSelect>
      </div>
    </div>

    <div class="flex h-[50px] items-center">
      <p class="w-1/3 text-base">{{ $t("printersettings.receipt-printer") }}</p>
      <div class="w-1/3">
        <SSelect v-model="receiptPrinter">
          <SSelectTrigger class="w-full">
            <SSelectValue :placeholder="$t('select.printer')" />
          </SSelectTrigger>
          <SSelectContent>
            <SSelectItem
              v-for="item in availablePrinters"
              :key="item.name"
              :value="item.name"
            >
              {{ item.displayName }}
            </SSelectItem>
          </SSelectContent>
        </SSelect>
      </div>
    </div>
  </div>
</template>

<script lang="ts">
import { defineComponent, ref, computed } from "vue";

// modules
import POSConfigModule from "@/store/modules/pos-config";
import PrinterConfigModule from "@/store/modules/printers-config";

// constants
import { getPrinters } from "@/api/group/printer";
import { TicketTypes } from "@/interfaces/receipts";

//interfaces
import { IPrinterInfo, IPrinterUsage } from "@/interfaces/printer/printer";

//helpers
import { getAvailablePrinters } from "@/utils/helpers/cross-platform";

import { useI18n } from "vue-i18n";
import {
  SButton,
  SSelect,
  SSelectContent,
  SSelectItem,
  SSelectTrigger,
  SSelectValue,
} from "@smeetz/ui-core";

export default defineComponent({
  name: "TicketsEmails",
  components: {
    SButton,
    SSelect,
    SSelectContent,
    SSelectItem,
    SSelectTrigger,
    SSelectValue,
  },
  setup() {
    const printers = computed(() => PrinterConfigModule.printers);
    const printerOption = ref<IPrinterUsage>({
      ...PrinterConfigModule.printerOption,
    });

    const { t } = useI18n({});

    let availablePrinters = ref<IPrinterInfo[]>([]);

    const receiptPrinter = ref<string | null>(
      PrinterConfigModule.receiptPrinter
    );
    const ticketPrinter = ref<string | null>(PrinterConfigModule.ticketPrinter);
    const labelPrinter = ref<string | null>(PrinterConfigModule.labelPrinter);
    const labelPrintingStatus = ref<boolean>(
      PrinterConfigModule.isLabelPrintingEnabled
    );

    // Order summary receipt
    const orderSummaryReceiptPrinter = ref<string | null>(
      PrinterConfigModule.orderSummaryReceiptPrinter
    );
    const orderSummaryReceiptPrintingStatus = ref<boolean>(
      PrinterConfigModule.isOrderSummaryReceiptEnabled
    );

    const showLocalPrinters = computed(() => {
      return PrinterConfigModule.isNativeTicketPrintingEnabled;
    });

    getPrinters(POSConfigModule.groupId as number)
      .then((printers) => {
        PrinterConfigModule.setPrinters(printers);
      })
      .catch((error) => {
        console.error(error);
      });

    const toggleStatus = (status: boolean) => {
      printerOption.value.auto = status;
    };

    const selectedTicketType = ref<TicketTypes>(
      PrinterConfigModule.ticketType || TicketTypes.Ticket
    );
    const ticketTypesArray = computed(() => {
      // Base ticket types always available
      const types = [
        {
          label: t("select.regular-ticket"),
          value: TicketTypes.Ticket,
        },
        {
          label: t("select.mini-ticket"),
          value: TicketTypes.MiniTicket,
        },
      ];

      // Add Edeis specific ticket types when local printing is enabled and client is Edeis
      if (showLocalPrinters.value) {
        types.push(
          {
            label: t("select.boca-ticket-large"),
            value: TicketTypes.EdeisLarge,
          },
          {
            label: t("select.boca-ticket-small"),
            value: TicketTypes.EdeisSmall,
          }
        );
      }

      return types;
    });
    const ticketPrintingStatus = ref<boolean>(
      PrinterConfigModule.isTicketPrintingEnabled
    );

    function setTicketType(type: TicketTypes) {
      PrinterConfigModule.setTicketType(type);
    }

    function setLabelPrintingStatus(status: boolean) {
      labelPrintingStatus.value = status;
    }

    function setTicketPrintingStatus(status: boolean) {
      ticketPrintingStatus.value = status;
    }

    function setOrderSummaryReceiptPrintingStatus(status: boolean) {
      orderSummaryReceiptPrintingStatus.value = status;
    }

    return {
      printers,
      printerOption,
      toggleStatus,
      availablePrinters,
      receiptPrinter,
      ticketPrinter,
      ticketTypesArray,
      selectedTicketType,
      setTicketType,
      TicketTypes,
      t,
      labelPrinter,
      labelPrintingStatus,
      setLabelPrintingStatus,
      ticketPrintingStatus,
      setTicketPrintingStatus,
      showLocalPrinters,
      orderSummaryReceiptPrinter,
      orderSummaryReceiptPrintingStatus,
      setOrderSummaryReceiptPrintingStatus,
    };
  },
  async created() {
    const availablePrinters = (await getAvailablePrinters()) || [];
    this.availablePrinters = availablePrinters;
  },
});
</script>
